import { LandingPageContent } from '../types';

const giftsForThreeYearOlds: LandingPageContent = {
	path: '/gifts/gifts-for-3-year-olds',
	seoTitle: 'Gifts for 3-Year-Olds — Personalized Ideas That Hold Attention',
	metaDescription:
		'Gift ideas for 3-year-olds, from personalized storybooks and coloring books to keepsakes. What works at this age, what does not, and why.',
	heading: 'Gifts for 3-Year-Olds',
	subheading: 'The age where seeing themselves is the entire appeal.',
	heroImage: '/assets/hero/kid-5/flat_scene.png',
	breadcrumbParents: [{ name: 'Gifts for kids', url: '/gifts-for-kids' }],
	intro: `Three is a specific and slightly awkward gifting age. The child has strong opinions but no consistency, a real attention span but a short one, and has just worked out that they are a distinct person in the world.

That last part is the useful bit. At three, a child recognising themselves in a book is not a novelty — it's genuinely absorbing.`,
	sections: [
		{
			heading: 'What works at three',
			body: `**Personalized storybooks.** Three-year-olds will sit through a short illustrated story, and will demand the same one repeatedly. A book where they are the hero gets re-read far past the point where an adult has stopped enjoying it.

**Personalized coloring books.** Fine motor control is arriving. It's messy, and that's fine — the pages are meant to be filled in badly and proudly.

**Anything with their name spoken aloud.** Three is often when children start recognising their own written name. Seeing it in print, in a book, attached to a character who looks like them, is a small but real moment.`,
		},
		{
			heading: 'What does not work at three',
			body: `- **Anything with small parts.** Still a genuine hazard at this age.
- **Complex rules.** Board games are a year or two away.
- **Long stories.** Twelve illustrated pages is about right. Thirty is not.
- **Screens as a gift.** Whatever your view on screen time generally, it makes a poor present — it's not something that can be unwrapped and held.`,
		},
		{
			heading: 'Attention span, honestly',
			body: `Expect five to ten minutes of sustained focus, and expect it to be non-negotiable when it happens.

This is why personalization works so well at three: it front-loads the interest. The child doesn't need to be persuaded to care about the character, because the character is them. You skip the part where a three-year-old decides they aren't interested in a story about a bear.`,
		},
		{
			heading: 'Art styles that suit this age',
			body: `Favour bold shapes and high contrast. **Flat modern** illustration and the **Pixar-like 3D** style both read clearly at a glance, which matters when the viewer is three and holding the book at an angle.

More delicate styles — watercolour especially — are beautiful, and better appreciated by the adult reading aloud than by the child being read to. If the book is intended as a keepsake for the parents as much as a gift for the child, that trade may be worth making.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'Twelve illustrated pages — about right for a three-year-old.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'Simple outline pages that suit developing motor control.',
		},
	],
	faqs: [
		{
			question: 'What is a good gift for a 3-year-old?',
			answer:
				'Personalized storybooks and coloring books work particularly well at three, because children this age are strongly drawn to recognising themselves. Keep stories short — around twelve illustrated pages — and choose bold, high-contrast art styles.',
		},
		{
			question: 'Are personalized books suitable for a 3-year-old?',
			answer:
				'Yes. Three is close to the ideal age. The child is old enough to follow a simple narrative and young enough to accept themselves as the hero without irony.',
		},
		{
			question: 'What art style is best for a young child?',
			answer:
				'Flat modern illustration and Pixar-style 3D read most clearly for young children, because both use bold shapes and strong contrast. Watercolour is subtler and is often appreciated more by the adult reading the book aloud.',
		},
	],
	related: [
		{ label: 'Gifts for kids', href: '/gifts-for-kids' },
		{ label: 'Birthday gifts for kids', href: '/gifts/birthday-gifts-for-kids' },
		{
			label: 'Personalized gifts for a 4-year-old',
			href: '/blog/personalized-gifts-for-4-year-olds',
		},
	],
	cta: { label: 'Browse gifts', href: '/products' },
};

export default giftsForThreeYearOlds;
